import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import TripOptionCard from 'Views/Components/_HumanWritten/TripOptionCard/TripOptionCard';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';

export type TripChoice = 'departing' | 'returning' | 'both';

export interface TripChoiceOption {
	value: TripChoice;
	title: React.ReactNode;
	/** One line per sailing: `Vancouver → Nanaimo · 30 Sep 2026, 13:00`. */
	routes: string[];
}

interface TripChoiceContentProps {
	mode: 'edit' | 'remove';
	options: TripChoiceOption[];
	initial: TripChoice;
	onConfirm: (choice: TripChoice) => void;
	onCancel: () => void;
}

/**
 * Laid out as the booking detailed view's `Edit booking` wizard (WizardModal): the same header, the
 * same trip cards, the same large footer buttons. The wording is design 12p's.
 */
function TripChoiceContent({ mode, options, initial, onConfirm, onCancel }: TripChoiceContentProps) {
	const [chosen, setChosen] = useState<TripChoice>(initial);
	const title = mode === 'edit' ? 'Edit booking' : 'Remove booking';

	return (
		<>
			<NewModal.Header className="wizard-modal__header">
				<div className="flex items-start justify-between gap-3 px-6 pt-5 pb-4">
					<h4 className="m-0 text-xl font-bold text-slate-900 leading-tight">{title}</h4>
					<button
						type="button"
						className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"
						onClick={onCancel}
						aria-label="Close"
					>
						<Icon name="close" useMaterialIcons />
					</button>
				</div>
				<div className="h-1 w-full bg-slate-100" />
			</NewModal.Header>

			<NewModal.Body className="wizard-modal__body">
				<div className="p-6 space-y-4">
					<div>
						<h3 className="text-lg font-semibold text-slate-900 m-0">
							{mode === 'edit' ? (
								<FormattedMessage
									id="class-trip-choice__edit-question"
									defaultMessage="Which trips would you like to change?"
								/>
							) : (
								<FormattedMessage
									id="class-trip-choice__remove-question"
									defaultMessage="Which trips would you like to remove?"
								/>
							)}
						</h3>
						{mode === 'remove' && (
							<div className="text-sm text-slate-500 mt-1">
								<FormattedMessage
									id="class-trip-choice__remove-warning"
									defaultMessage="Removing releases the seats and can't be undone."
								/>
							</div>
						)}
					</div>
					<div className="space-y-3">
						{options.map(option => (
							<TripOptionCard
								key={option.value}
								selected={chosen === option.value}
								onClick={() => setChosen(option.value)}
								title={option.title}
								routes={option.routes}
								testId={`class-trip-choice-${option.value}`}
							/>
						))}
					</div>
				</div>
			</NewModal.Body>

			<NewModal.Footer className="wizard-modal__footer">
				<Button
					newStyles
					display={Display.Outline}
					colors={Colors.Grey}
					sizes={Sizes.Large}
					onClick={onCancel}
					buttonProps={{ 'data-test-id': 'class-trip-choice-cancel' } as any}
				>
					Cancel
				</Button>
				<Button
					newStyles
					display={Display.Solid}
					colors={Colors.Secondary}
					sizes={Sizes.Large}
					onClick={() => onConfirm(chosen)}
					buttonProps={{ 'data-test-id': 'class-trip-choice-confirm' } as any}
				>
					{mode === 'edit' ? 'Edit' : 'Remove'}
				</Button>
			</NewModal.Footer>
		</>
	);
}

/**
 * Design 12p: one `Edit` / `Remove` per booking, and a single-step modal that asks which trips it
 * applies to — departing, returning, or both — and acts on the answer. For remove it is also the
 * confirmation, so there is no second "are you sure".
 *
 * Resolves with the choice, or `undefined` when the booker cancels or closes it.
 */
export async function tripChoiceModal(
	mode: 'edit' | 'remove',
	options: TripChoiceOption[],
): Promise<TripChoice | undefined> {
	const initial = options.some(x => x.value === 'both') ? 'both' : options[0].value;
	let choice: TripChoice = initial;

	try {
		const confirmed = await newConfirmModal(
			mode === 'edit' ? 'Edit booking' : 'Remove booking',
			(confirm, cancel) => (
				<TripChoiceContent
					mode={mode}
					options={options}
					initial={initial}
					onConfirm={value => {
						choice = value;
						confirm();
					}}
					onCancel={cancel}
				/>
			),
			{ resolveOnCancel: true, className: 'wizard-modal', shouldCloseOnEsc: true },
		);
		return confirmed ? choice : undefined;
	} catch {
		return undefined;
	}
}
